import { Dispatch, SetStateAction, useCallback } from "react";
import { ButtonProps } from "../Button";
import ToggleButton from "../ToggleButton/ToggleButton";

type Collection<T> = T[] | Set<T>;

type ToggleButtonGroupProps<
  T extends string | number | boolean | null | undefined,
  U extends string | number | boolean | null | undefined
> = {
  options: ({ id: T; label?: U } & Omit<ButtonProps, "children">)[];
  minOneSelected?: boolean;
  style?: React.CSSProperties;
  buttonStyle?: React.CSSProperties;
} & (
  | {
      selected: T;
      setSelected: Dispatch<SetStateAction<T>>;
    }
  | {
      selected: Collection<T>;
      setSelected: Dispatch<SetStateAction<Collection<T>>>;
    }
);
export default function ToggleButtonGroup<
  T extends string | number | boolean | null | undefined,
  U extends string | number | boolean | null | undefined
>({
  options,
  selected,
  setSelected,
  minOneSelected,
  style,
  buttonStyle,
}: ToggleButtonGroupProps<T, U>) {
  const handleClick = useCallback(
    (clickedOption: T) => {
      if (Array.isArray(selected)) {
        (setSelected as Dispatch<SetStateAction<T[]>>)((prevSelected) => {
          if (prevSelected.includes(clickedOption)) {
            return minOneSelected && prevSelected.length === 1
              ? prevSelected
              : prevSelected.filter((item) => item !== clickedOption);
          } else {
            return [...prevSelected, clickedOption];
          }
        });
      } else if (isSet(selected)) {
        (setSelected as Dispatch<SetStateAction<Set<T>>>)((prevSelected) => {
          const newSet = new Set(prevSelected as Set<T>);
          if (newSet.has(clickedOption)) {
            if (minOneSelected && newSet.size === 1) {
              return newSet;
            }
            newSet.delete(clickedOption);
          } else {
            newSet.add(clickedOption);
          }
          return newSet;
        });
      } else {
        (setSelected as Dispatch<SetStateAction<T>>)((prevSelected) => {
          if (prevSelected !== clickedOption || minOneSelected)
            return clickedOption;
          return null;
        });
      }
    },
    [selected, setSelected, minOneSelected]
  );

  const isSelected = useCallback(
    (option: T) => {
      if (Array.isArray(selected)) return selected.includes(option);
      else if (isSet(selected)) return selected.has(option);
      return option === selected;
    },
    [selected]
  );

  return (
    <div
      style={{
        display: "flex",
        gap: "0.5rem",
        ...style,
      }}
    >
      {options.map(({ id, label, ...buttonProps }) => (
        <ToggleButton
          key={String(id)}
          isSelected={isSelected(id)}
          onClick={() => {
            handleClick(id);
          }}
          {...buttonProps}
          style={{
            ...buttonStyle,
            ...buttonProps.style,
          }}
        >
          {label ?? id}
        </ToggleButton>
      ))}
    </div>
  );
}

const isSet = <T,>(
  option: string | number | boolean | Collection<T>
): option is Set<T> =>
  typeof option === "object" && option !== null && "has" in option;
